import { useCallback, useEffect, useRef, useState } from "react";
import { apiSetup, type SetupStatus } from "../lib/api";
import { Logo } from "../components/Logo";
import { dateTimeBR } from "../lib/format";
import { useTheme } from "../hooks/useTheme";

// Painel de configuração da conexão ao SQL Server (roda no PC servidor, via #config).
// Fluxo: preenche os dados → Testar conexão → Salvar e conectar (grava o config.json e
// reconecta o servidor sem reiniciar).
type Msg = { tipo: "erro" | "ok" | "info"; texto: string } | null;

export function Configuracao() {
  const { theme, toggle } = useTheme();

  const [server, setServer] = useState("");
  const [porta, setPorta] = useState("");
  const [database, setDatabase] = useState("");
  const [user, setUser] = useState("");
  const [password, setPassword] = useState("");
  const [trust, setTrust] = useState(true);
  const [encrypt, setEncrypt] = useState(false);

  const [status, setStatus] = useState<SetupStatus | null>(null);
  const [msg, setMsg] = useState<Msg>(null);
  const [busy, setBusy] = useState(false);

  const refServer = useRef<HTMLInputElement>(null);
  const refPorta = useRef<HTMLInputElement>(null);
  const refDatabase = useRef<HTMLInputElement>(null);
  const refUser = useRef<HTMLInputElement>(null);
  const refPassword = useRef<HTMLInputElement>(null);

  const carregar = useCallback(() => {
    apiSetup
      .status()
      .then((s) => {
        setStatus(s);
        // Pré-preenche com o que já está salvo (a senha nunca volta do servidor).
        setServer(s.sql.server || "");
        setPorta(s.sql.port ? String(s.sql.port) : "");
        setDatabase(s.sql.database || "");
        setUser(s.sql.user || "");
        setTrust(s.sql.options?.trustServerCertificate !== false);
        setEncrypt(s.sql.options?.encrypt === true);
      })
      .catch((e) => setMsg({ tipo: "erro", texto: (e as Error).message }));
  }, []);

  useEffect(() => {
    carregar();
    setTimeout(() => refServer.current?.focus(), 40);
  }, [carregar]);

  const montarPayload = () => ({
    server: server.trim(),
    port: porta ? Number(porta) : null,
    database: database.trim(),
    user: user.trim(),
    password,
    options: { trustServerCertificate: trust, encrypt },
  });

  const testar = useCallback(async () => {
    setBusy(true);
    setMsg({ tipo: "info", texto: "Testando conexão…" });
    try {
      const r = await apiSetup.testar(montarPayload());
      if (r.ok) setMsg({ tipo: "ok", texto: `Conexão OK${r.agora ? ` — servidor respondeu ${dateTimeBR(r.agora)}` : ""}.` });
      else setMsg({ tipo: "erro", texto: r.erro ?? "Falha na conexão." });
    } catch (e) {
      setMsg({ tipo: "erro", texto: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }, [server, porta, database, user, password, trust, encrypt]);

  const salvar = useCallback(async () => {
    setBusy(true);
    setMsg({ tipo: "info", texto: "Testando e salvando…" });
    try {
      const r = await apiSetup.salvar(montarPayload());
      if (r.ok) {
        setMsg({ tipo: "ok", texto: "Configuração salva e conectada. O sistema já está pronto para uso." });
        carregar();
      } else {
        setMsg({ tipo: "erro", texto: r.erro ?? "Não foi possível salvar." });
      }
    } catch (e) {
      setMsg({ tipo: "erro", texto: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }, [server, porta, database, user, password, trust, encrypt, carregar]);

  // Enter = Tab (regra de formulário): salta para o próximo campo; no último, salva.
  const enterPara = (proximo: React.RefObject<HTMLElement> | null) => (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (proximo) proximo.current?.focus();
      else salvar();
    }
  };

  const soDigitos = (v: string) => v.replace(/\D+/g, "");

  return (
    <div className="app cfg">
      <header className="app-header">
        <div className="brand">
          <Logo />
          <h1>
            Configuração do Servidor
            <small>Conexão com o SQL Server · Painel de Produção FORMSTEEL</small>
          </h1>
        </div>
        <span className="spacer" />
        <button className="btn-secundario" onClick={toggle} title="Alternar tema">{theme === "light" ? "Modo escuro" : "Modo claro"}</button>
      </header>

      <div className="cfg-body">
        <section className="cfg-card">
          {status && (
            <div className={`cfg-status ${status.conectado ? "ok" : status.configurado ? "warn" : "off"}`}>
              <span className="cfg-status-dot" />
              {status.conectado
                ? "Conectado ao banco de dados."
                : status.configurado
                  ? `Configurado, mas sem conexão no momento${status.erro ? ` — ${status.erro}` : ""}.`
                  : "Ainda não configurado. Preencha os dados abaixo."}
            </div>
          )}

          <div className="cfg-form">
            <label className="cfg-wide">
              <span>Servidor <em>(IP, nome, ou IP\INSTANCIA — ex.: 192.168.0.10 ou SERVIDOR\SQLEXPRESS)</em></span>
              <input
                ref={refServer}
                value={server}
                onChange={(e) => setServer(e.target.value)}
                onKeyDown={enterPara(refPorta)}
                placeholder="192.168.0.10\SQLEXPRESS"
                autoComplete="off"
                disabled={busy}
              />
            </label>

            <label>
              <span>Porta <em>(opcional)</em></span>
              <input
                ref={refPorta}
                value={porta}
                inputMode="numeric"
                onChange={(e) => setPorta(soDigitos(e.target.value))}
                onKeyDown={enterPara(refDatabase)}
                placeholder="1433"
                autoComplete="off"
                disabled={busy}
              />
            </label>

            <label>
              <span>Banco de dados</span>
              <input
                ref={refDatabase}
                value={database}
                onChange={(e) => setDatabase(e.target.value)}
                onKeyDown={enterPara(refUser)}
                placeholder="FORMSTEEL"
                autoComplete="off"
                disabled={busy}
              />
            </label>

            <label>
              <span>Usuário</span>
              <input
                ref={refUser}
                value={user}
                onChange={(e) => setUser(e.target.value)}
                onKeyDown={enterPara(refPassword)}
                placeholder="sa"
                autoComplete="off"
                disabled={busy}
              />
            </label>

            <label>
              <span>Senha {status?.sql.temSenha && !password && <em>(salva — deixe em branco para manter)</em>}</span>
              <input
                ref={refPassword}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={enterPara(null)}
                autoComplete="off"
                disabled={busy}
              />
            </label>

            <div className="cfg-opcoes">
              <label className="cfg-check">
                <input type="checkbox" checked={trust} onChange={(e) => setTrust(e.target.checked)} disabled={busy} />
                <span>Confiar no certificado do servidor</span>
              </label>
              <label className="cfg-check">
                <input type="checkbox" checked={encrypt} onChange={(e) => setEncrypt(e.target.checked)} disabled={busy} />
                <span>Criptografar conexão (encrypt)</span>
              </label>
            </div>
          </div>

          {msg && <div className={`cfg-msg ${msg.tipo}`}>{msg.texto}</div>}

          <div className="cfg-acoes">
            <button className="btn-secundario" onClick={testar} disabled={busy}>Testar conexão</button>
            <button className="btn-acao verde" onClick={salvar} disabled={busy}>Salvar e conectar</button>
          </div>

          <p className="cfg-nota">
            Por segurança, a gravação só é permitida no próprio servidor (localhost). Os dados
            ficam no <code>config.json</code> ao lado do executável.
          </p>
        </section>
      </div>
    </div>
  );
}
